import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { GradientIcon } from '../gradients/GradientIcon';
import { Review } from 'functions/src/types/firestore/Game/Review';
import { useDateFormatter } from 'src/hooks/useDateFormatter';
import { NUMERIC } from 'src/util/dates/presets/dates';

export type ReviewFooterProps = Pick<
  Review<Date>,
  'date' | 'likesCount' | 'dislikesCount' | 'gameVersion'
>;

export function ReviewFooter({
  likesCount = 0,
  dislikesCount = 0,
  date,
  gameVersion,
}: ReviewFooterProps) {
  const [reactions, setReactions] = useState({
    currLikes: likesCount,
    currDislikes: dislikesCount,
    isLikeActive: false,
    isDislikeActive: false,
  });

  const format = useDateFormatter({
    dateFormat: NUMERIC,
    timeFormat: undefined,
    separator: '',
  });

  const dateFormatted = date ? format(date) : '';

  function handleLike() {
    if (
      (!reactions.isLikeActive && !reactions.isDislikeActive) ||
      reactions.isLikeActive
    ) {
      setReactions({
        ...reactions,
        isLikeActive: !reactions.isLikeActive,
        currLikes: !reactions.isLikeActive
          ? reactions.currLikes + 1
          : reactions.currLikes - 1,
      });
    }

    if (reactions.isDislikeActive) {
      setReactions({
        currDislikes: reactions.currDislikes - 1,
        isDislikeActive: false,
        currLikes: reactions.currLikes + 1,
        isLikeActive: true,
      });
    }
  }

  function handleDislike() {
    if (
      (!reactions.isDislikeActive && !reactions.isLikeActive) ||
      reactions.isDislikeActive
    ) {
      setReactions({
        ...reactions,
        isDislikeActive: !reactions.isDislikeActive,
        currDislikes: !reactions.isDislikeActive
          ? reactions.currDislikes + 1
          : reactions.currDislikes - 1,
      });
    }

    if (reactions.isLikeActive) {
      setReactions({
        currLikes: reactions.currLikes - 1,
        isLikeActive: false,
        currDislikes: reactions.currDislikes + 1,
        isDislikeActive: true,
      });
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            marginRight: '4px',
            color: (theme) => theme.palette.text.secondary,
            textTransform: 'uppercase',
          }}
        >
          {dateFormatted}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            opacity: '0.5',
            textTransform: 'uppercase',
            pl: '8px',
          }}
        >
          {gameVersion}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            onClick={handleLike}
            sx={{
              display: 'flex',
            }}
          >
            <GradientIcon
              sx={{
                transform: 'scale(1.3)',
                height: '19.96px',
                width: '18.44px',
                cursor: 'pointer',
              }}
              IconComponent={
                reactions.isLikeActive ? ThumbUpAltIcon : ThumbUpOffAltIcon
              }
            />
          </Box>

          <Typography
            sx={{
              marginLeft: '4.44px',
            }}
          >
            {reactions.currLikes}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex' }} onClick={handleDislike}>
            <GradientIcon
              sx={{
                marginLeft: '30px',
                transform: 'scaleX(-1) scale(1.3)',
                height: '19.96px',
                width: '18.44px',
                cursor: 'pointer',
              }}
              IconComponent={
                reactions.isDislikeActive
                  ? ThumbDownAltIcon
                  : ThumbDownOffAltIcon
              }
            />
          </Box>
          <Typography
            sx={{
              marginLeft: '4.44px',
            }}
          >
            {reactions.currDislikes}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
